import type { WeatherStats } from './weather'

// Composite insight cards for the Monitoring tab's Insights view. Status is
// judged against a per-day-of-year climatology (previous years), so it means
// "unusual for this date". The selected range (All / 1Y / 1M) sets the value
// shown: All = current reading, a window = the mean over that window vs its
// own norm.

export type MetricKey =
  | 'swc'
  | 'lst'
  | 'ndvi'
  | 'biomass'
  | 'canopyCover'
  | 'canopyHeight'
  | 'carbonDensity'

export interface RealPoint {
  date: string
  mean: number
  valid_percent?: number | null
}

const DAY = 86_400_000
const YEAR = 365 * DAY
const CLIM_WINDOW_DAYS = 45
const CLIM_EXCLUDE_MS = 330 * DAY
const MIN_CLIM_SAMPLES = 5
const MIN_VALID_PERCENT = 30
// Planet carbon is aboveground only; the uplift adds belowground (root)
// biomass. 1.13 is the cocoa root-to-shoot ratio — a per-project/crop setting
// once non-cocoa projects use this insight.
const ROOT_UPLIFT = 1.13

export interface XY {
  x: number
  y: number
}

export type InsightStatus = 'good' | 'watch' | 'alert'

export interface SparkSeries {
  metric: MetricKey
  label: string
  points: XY[] // normalised to [0,1] so mixed units share one axis
}

export interface InsightVM {
  key: string
  title: string
  status: InsightStatus | null
  headline: string
  details: string[]
  spark: SparkSeries[] | null
  waiting: boolean
}

function mean(xs: number[]): number {
  return xs.reduce((s, v) => s + v, 0) / xs.length
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

function clean(points: RealPoint[] | null): XY[] {
  if (!points) return []
  const kept = points
    .filter((p) => p.valid_percent == null || p.valid_percent >= MIN_VALID_PERCENT)
    .map((p) => ({ x: Date.parse(p.date), y: p.mean }))
  return kept.map((p, i) =>
    i === 0 || i === kept.length - 1
      ? p
      : { x: p.x, y: median([kept[i - 1].y, p.y, kept[i + 1].y]) },
  )
}

function doy(ms: number): number {
  const d = new Date(ms)
  return Math.floor((ms - Date.UTC(d.getUTCFullYear(), 0, 1)) / DAY)
}

function doyDist(a: number, b: number): number {
  const d = Math.abs(a - b)
  return Math.min(d, 365 - d)
}

function doyInSpan(d: number, s: number, e: number): boolean {
  return s <= e ? d >= s && d <= e : d >= s || d <= e
}

interface Clim {
  mean: number
  std: number
}

function climatology(pts: XY[], atMs: number): Clim | null {
  const target = doy(atMs)
  const sel = pts.filter(
    (p) => atMs - p.x > CLIM_EXCLUDE_MS && doyDist(doy(p.x), target) <= CLIM_WINDOW_DAYS,
  )
  if (sel.length < MIN_CLIM_SAMPLES) return null
  const m = mean(sel.map((p) => p.y))
  const std = Math.sqrt(mean(sel.map((p) => (p.y - m) ** 2)))
  return { mean: m, std }
}

export interface Stat {
  value: number
  z: number | null
  pct: number | null
  clim: Clim | null
}

// The reading for the selected window: All (windowMs null) = latest point vs
// its seasonal norm; a window = the window mean vs the same calendar window in
// prior years. Falls back to the latest point when the window has no data
// (e.g. annual products under a 1-month range).
function windowStat(pts: XY[], windowMs: number | null, nowMs: number): Stat | null {
  if (!pts.length) return null
  const last = pts[pts.length - 1]
  if (windowMs == null) {
    const c = climatology(pts, last.x)
    return statVs(last.y, c)
  }
  const start = nowMs - windowMs
  const win = pts.filter((p) => p.x >= start && p.x <= nowMs)
  if (!win.length) {
    const c = climatology(pts, last.x)
    return statVs(last.y, c)
  }
  const value = mean(win.map((p) => p.y))
  const fullYear = windowMs >= 360 * DAY
  const sD = doy(start)
  const eD = doy(nowMs)
  const hist = pts.filter((p) => p.x < start && (fullYear || doyInSpan(doy(p.x), sD, eD)))
  if (hist.length < MIN_CLIM_SAMPLES) return { value, z: null, pct: null, clim: null }
  const m = mean(hist.map((p) => p.y))
  const std = Math.sqrt(mean(hist.map((p) => (p.y - m) ** 2)))
  return statVs(value, { mean: m, std })
}

function statVs(value: number, clim: Clim | null): Stat {
  if (!clim) return { value, z: null, pct: null, clim: null }
  const z = clim.std > 1e-9 ? (value - clim.mean) / clim.std : 0
  const pct = Math.abs(clim.mean) > 1e-9 ? ((value - clim.mean) / Math.abs(clim.mean)) * 100 : null
  return { value, z, pct, clim }
}

// Min–max to [0,1] so series with different units (%, °C, index) overlay on one
// axis and only their shapes are compared. Needs ≥2 points to draw a line.
function spark(metric: MetricKey, label: string, pts: XY[]): SparkSeries | null {
  if (pts.length < 2) return null
  const ys = pts.map((p) => p.y)
  const lo = Math.min(...ys)
  const hi = Math.max(...ys)
  const d = hi - lo
  return { metric, label, points: pts.map((p) => ({ x: p.x, y: d > 1e-9 ? (p.y - lo) / d : 0.5 })) }
}

function sparks(...s: (SparkSeries | null)[]): SparkSeries[] | null {
  const out = s.filter((x): x is SparkSeries => x != null)
  return out.length ? out : null
}

interface Step {
  date: number
  from: number
  to: number
}

function detectStep(pts: XY[]): Step | null {
  if (pts.length < 8) return null
  const recent = pts.slice(-3)
  const recentMed = median(recent.map((p) => p.y))
  const base = pts.filter((p) => p.x < recent[0].x && recent[0].x - p.x <= YEAR)
  if (base.length < 5) return null
  const baseVals = base.map((p) => p.y)
  const baseMed = median(baseVals)
  const mad = median(baseVals.map((v) => Math.abs(v - baseMed)))
  const threshold = Math.max(2 * 1.4826 * mad, Math.abs(baseMed) * 0.05)
  if (baseMed - recentMed <= threshold) return null
  let start = pts.length - 1
  while (start > 0 && baseMed - pts[start - 1].y > threshold) start--
  return { date: pts[start].x, from: baseMed, to: recentMed }
}

function yearOverYear(pts: XY[]): number | null {
  const last = pts[pts.length - 1]
  if (!last) return null
  const target = last.x - YEAR
  let prev: XY | null = null
  let best = 20 * DAY
  for (const p of pts) {
    const dist = Math.abs(p.x - target)
    if (dist < best) {
      best = dist
      prev = p
    }
  }
  if (!prev || Math.abs(prev.y) < 1e-9) return null
  return ((last.y - prev.y) / Math.abs(prev.y)) * 100
}

function shortMonth(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short' })
}

// " (+43% vs norm)" style suffix; empty when the deviation is trivial or unknown.
function pctSuffix(pct: number | null): string {
  if (pct == null || Math.abs(pct) < 1) return ''
  return ` (${pct >= 0 ? '+' : ''}${Math.round(pct)}% vs norm)`
}

function degSuffix(s: Stat): string {
  if (!s.clim) return ''
  const d = s.value - s.clim.mean
  if (Math.abs(d) < 0.5) return ''
  return ` (${d >= 0 ? '+' : ''}${d.toFixed(1)}° vs norm)`
}

interface Ctx {
  weather: WeatherStats | null
  areaHa: number
  windowMs: number | null
  nowMs: number
}

function waterCard(series: SeriesMap, ctx: Ctx): InsightVM {
  const swc = clean(series.swc ?? null)
  const lst = clean(series.lst ?? null)
  const card: InsightVM = {
    key: 'water',
    title: 'Water',
    status: null,
    headline: '',
    details: [],
    spark: null,
    waiting: !swc.length,
  }
  if (card.waiting) return card
  card.spark = sparks(spark('swc', 'Soil water', swc), spark('lst', 'Temp', lst))

  const sw = windowStat(swc, ctx.windowMs, ctx.nowMs)
  const lt = windowStat(lst, ctx.windowMs, ctx.nowMs)
  const rainDelta = ctx.weather?.agro.seasonRainDeltaPct ?? null
  const hot = lt?.z != null && lt.z >= 1
  const deficit = rainDelta != null && rainDelta <= -20

  if (sw?.z == null) card.status = 'good'
  else if (sw.z <= -1.5 && (hot || deficit)) card.status = 'alert'
  else if (sw.z <= -1 || (lt?.z != null && lt.z >= 1.5)) card.status = 'watch'
  else card.status = 'good'

  card.headline =
    card.status === 'alert'
      ? 'Water stress likely'
      : card.status === 'watch'
        ? sw && sw.z != null && sw.z <= -1
          ? 'Soil drier than normal'
          : 'Running hot for the season'
        : 'No water stress'

  if (sw) card.details.push(`Soil water ${sw.value.toFixed(1)}%${pctSuffix(sw.pct)}`)
  if (lt) card.details.push(`Temp ${lt.value.toFixed(1)}°C${degSuffix(lt)}`)
  if (ctx.weather && rainDelta != null) {
    card.details.push(
      `Rain ${ctx.weather.agro.seasonRainMm} mm (${rainDelta >= 0 ? '+' : ''}${rainDelta}%)`,
    )
  }
  return card
}

function cropCard(series: SeriesMap, ctx: Ctx): InsightVM {
  const ndvi = clean(series.ndvi ?? null)
  const biomass = clean(series.biomass ?? null)
  const card: InsightVM = {
    key: 'crop',
    title: 'Crop condition',
    status: null,
    headline: '',
    details: [],
    spark: null,
    waiting: !ndvi.length && !biomass.length,
  }
  if (card.waiting) return card
  // NDVI is often a single ordered scene — fall back to the biomass index,
  // which carries the same meaning with a continuous history.
  const p =
    ndvi.length >= 8
      ? { pts: ndvi, metric: 'ndvi' as MetricKey, label: 'NDVI', dp: 2 }
      : { pts: biomass, metric: 'biomass' as MetricKey, label: 'Biomass', dp: 2 }
  card.spark = sparks(spark('ndvi', 'NDVI', ndvi), spark('biomass', 'Biomass', biomass))

  const st = windowStat(p.pts, ctx.windowMs, ctx.nowMs)
  const yoy = yearOverYear(p.pts)

  if (st?.z == null) card.status = 'good'
  else if (st.z <= -2) card.status = 'alert'
  else if (st.z <= -1) card.status = 'watch'
  else card.status = 'good'

  card.headline =
    card.status === 'alert'
      ? 'Vegetation well below normal'
      : card.status === 'watch'
        ? 'Vegetation below normal'
        : 'Developing normally'

  if (st) card.details.push(`${p.label} ${st.value.toFixed(p.dp)}${pctSuffix(st.pct)}`)
  if (yoy != null && Math.abs(yoy) >= 2) {
    card.details.push(`${yoy >= 0 ? '+' : ''}${Math.round(yoy)}% vs last season`)
  }
  return card
}

function forestCard(series: SeriesMap, ctx: Ctx): InsightVM {
  const canopy = clean(series.canopyCover ?? null)
  const carbon = clean(series.carbonDensity ?? null)
  const card: InsightVM = {
    key: 'forest',
    title: 'Forest & carbon',
    status: null,
    headline: '',
    details: [],
    spark: null,
    waiting: !canopy.length && !carbon.length,
  }
  if (card.waiting) return card
  card.spark = sparks(spark('canopyCover', 'Canopy', canopy), spark('carbonDensity', 'Carbon', carbon))

  const step = detectStep(canopy) ?? detectStep(carbon)
  if (step) {
    const both = detectStep(canopy) && detectStep(carbon)
    card.status = both ? 'alert' : 'watch'
    card.headline = both
      ? `Possible clearing in ${shortMonth(step.date)}`
      : `Canopy dropped in ${shortMonth(step.date)}`
  } else {
    card.status = 'good'
    card.headline = 'Forest cover stable'
  }

  const lastCanopy = canopy[canopy.length - 1]
  if (lastCanopy) card.details.push(`Canopy ${lastCanopy.y.toFixed(0)}%`)
  const lastCarbon = carbon[carbon.length - 1]
  if (lastCarbon && ctx.areaHa > 0) {
    const t = Math.round(lastCarbon.y * ROOT_UPLIFT * ctx.areaHa)
    card.details.push(`≈ ${t.toLocaleString('en-US')} t carbon incl. roots`)
  }
  return card
}

type SeriesMap = Partial<Record<MetricKey, RealPoint[] | null>>

export function buildInsights(
  series: SeriesMap,
  weather: WeatherStats | null,
  areaHa: number,
  windowMs: number | null,
  nowMs: number,
): InsightVM[] {
  const ctx: Ctx = { weather, areaHa, windowMs, nowMs }
  return [waterCard(series, ctx), cropCard(series, ctx), forestCard(series, ctx)]
}
